import {
  listFormFieldMetadata,
  listCostCenter,
  listCountries,
  listAuditDetails,
  listFpnProductTypes,
} from '../../graphql/queries';
import { FormFieldMetadataResponse, CostCenter, Country, FpnProductType } from '../../types/common/api.types';
import { AuditLogEntry } from '../../types/audit-logs/auditLogs.types';
import { formatApiError } from '../../utils/errorUtils';
import { logger } from '../../utils/logger';
import { fetchGraphQLRequest } from './clients/graphqlClient';

/**
 * Common Service
 * Handles all common/generic API operations that are shared across the application
 * This includes form field metadata, lookup data, and other shared resources
 */

// ============================================================================
// Form Field Metadata Operations
// ============================================================================

/**
 * Fetches form field metadata from the API
 * This includes field names, labels, and available options for form fields
 * @returns Promise resolving to form field metadata
 * @throws Error if API call fails
 */
export const fetchFormFieldMetadataAPI = async (): Promise<FormFieldMetadataResponse> => {
  try {
    logger.info('Fetching form field metadata from API');

    const result = await fetchGraphQLRequest<{ listFormFieldMetadata: FormFieldMetadataResponse }>({
      query: listFormFieldMetadata,
      variables: {},
      requestName: 'listFormFieldMetadata',
      requestType: 'query',
    });

    const formFieldMetadata = result.listFormFieldMetadata;

    logger.info('Successfully fetched form field metadata from API', {
      fieldCount: formFieldMetadata?.fields?.length ?? 0,
    });

    return formFieldMetadata;
  } catch (error) {
    logger.error('Error fetching form field metadata from API:', { error });
    throw formatApiError('Failed to fetch form field metadata', error);
  }
};

/**
 * Fetches cost center list from the API
 * This includes cost center codes and names
 * @returns Promise resolving to array of cost centers
 * @throws Error if API call fails
 */
export const fetchCostCentersAPI = async (): Promise<CostCenter[]> => {
  try {
    logger.info('Fetching cost centers from API');

    const result = await fetchGraphQLRequest<{ listCostCenter: CostCenter[] }>({
      query: listCostCenter,
      variables: {},
      requestName: 'listCostCenter',
      requestType: 'query',
    });

    const costCenters = result.listCostCenter || [];

    logger.info('Successfully fetched cost centers from API', {
      count: costCenters.length,
    });

    return costCenters;
  } catch (error) {
    logger.error('Error fetching cost centers from API:', { error });
    throw formatApiError('Failed to fetch cost centers', error);
  }
};

// ============================================================================
// Country Operations
// ============================================================================

/**
 * Fetches country list from the API
 * This includes country names and codes
 * @returns Promise resolving to array of countries
 * @throws Error if API call fails
 */
export const fetchCountriesAPI = async (): Promise<Country[]> => {
  try {
    logger.info('Fetching countries from API');

    const result = await fetchGraphQLRequest<{ listCountries: Country[] }>({
      query: listCountries,
      variables: {},
      requestName: 'listCountries',
      requestType: 'query',
    });

    const countries = result.listCountries || [];

    logger.info('Successfully fetched countries from API', {
      count: countries.length,
    });

    return countries;
  } catch (error) {
    logger.error('Error fetching countries from API:', { error });
    throw formatApiError('Failed to fetch countries', error);
  }
};

/**
 * Fetches PRODUCT_TYPE classification for every FPN in a single call.
 * An FPN may appear multiple times if it has multiple PRODUCT_TYPE rows.
 *
 * Fails open: if the backend query is not yet deployed (or errors), this resolves to an empty
 * array rather than throwing, so consumers fall back to "no classification" (show everything)
 * instead of breaking the page.
 *
 * @returns Promise resolving to an array of { fpn_id, product_type }
 */
export const fetchFpnProductTypesAPI = async (): Promise<FpnProductType[]> => {
  try {
    logger.info('Fetching FPN product types from API');

    const result = await fetchGraphQLRequest<{ listFpnProductTypes: FpnProductType[] }>({
      query: listFpnProductTypes,
      variables: {},
      requestName: 'listFpnProductTypes',
      requestType: 'query',
    });

    const productTypes = result.listFpnProductTypes || [];

    logger.info('Successfully fetched FPN product types from API', { count: productTypes.length });

    return productTypes;
  } catch (error) {
    // Fail open: log and return empty so the dropdown shows all FPNs until the backend exists.
    logger.error('Error fetching FPN product types from API (failing open with empty list):', { error });
    return [];
  }
};

// ============================================================================
// Audit Logs Operations
// ============================================================================

/**
 * Fetches audit logs from the API
 * This includes audit trail of system operations
 * @returns Promise resolving to array of audit log entries
 * @throws Error if API call fails
 */
export const fetchAuditLogsAPI = async (): Promise<AuditLogEntry[]> => {
  try {
    const result = await fetchGraphQLRequest<{ listAuditDetails: AuditLogEntry[] }>({
      query: listAuditDetails,
      variables: {},
      requestName: 'listAuditDetails',
      requestType: 'query',
    });

    const auditLogs = result.listAuditDetails || [];

    logger.info('Successfully fetched audit logs from API', {
      count: auditLogs.length,
    });

    return auditLogs;
  } catch (error) {
    logger.error('Error fetching audit logs from API:', { error });
    throw formatApiError('Failed to fetch audit logs', error);
  }
};
